import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = () => {
  const [isHovered, setIsHovered] = useState(false);
  const location = useLocation();
  const { user, userProfile, signOut } = useAuth();

  const sidebarItems = [
    { 
      label: 'History', 
      path: '/validation-history', 
      icon: 'Clock',
      tooltip: 'View past validation results'
    },
    { 
      label: 'Settings', 
      path: '/account-settings', 
      icon: 'Settings',
      tooltip: 'Manage your account settings'
    },
    {
      label: 'API Key',
      path: '/docs',
      icon: 'Key',
      tooltip: 'View your API key and documentation'
    }
  ];

  // Add admin dashboard link for admin users
  if (userProfile?.role === 'admin') {
    sidebarItems.push({
      label: 'Admin Dashboard',
      path: '/admin-dashboard',
      icon: 'Shield',
      tooltip: 'Access admin panel and manage system'
    });
  }

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Hover trigger area */}
      <div 
        className="px-4 py-2 bg-primary rounded-lg flex items-center space-x-2 cursor-pointer transition-all duration-300 hover:bg-primary/80 shadow-lg"
        onClick={() => setIsHovered(!isHovered)}
      >
        <Icon name="Menu" size={18} color="white" />
        <span className="text-sm font-medium text-white">Menu</span>
      </div>

      {/* Sidebar content */}
      <div 
        className={`absolute top-12 right-0 bg-surface border border-border shadow-lg rounded-lg transition-all duration-300 ease-in-out overflow-hidden min-w-72 ${
          isHovered ? 'w-72 visible opacity-100' : 'w-0 invisible opacity-0'
        }`}
      >
        <div className="p-6 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-md">
                <Icon name="Mail" size={20} color="white" />
              </div>
              <span className="text-lg font-semibold text-text-primary">
                Menu
              </span>
            </div>
            <button
              onClick={() => setIsHovered(false)}
              className="p-2 rounded-md hover:bg-gray-100 transition-colors"
            >
              <Icon name="X" size={18} />
            </button>
          </div>

          {/* Navigation Items */}
          <nav className="space-y-2">
            {sidebarItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 text-base ${
                  isActiveRoute(item.path)
                    ? 'bg-primary text-white shadow-md' 
                    : 'text-text-secondary hover:text-primary hover:bg-gray-50'
                }`}
                title={item.tooltip}
                onClick={() => setIsHovered(false)}
              >
                <Icon name={item.icon} size={20} />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* User Section */}
          {user && (
            <div className="border-t border-border pt-4 mt-4">
              <div className="px-4 py-3 bg-gray-50 rounded-lg mb-3">
                <div className="text-sm text-text-secondary">
                  Welcome back,
                </div>
                <div className="text-sm font-medium text-text-primary truncate">
                  {user.email}
                </div>
              </div>
              
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200"
              >
                <Icon name="LogOut" size={18} />
                <span>Logout</span>
              </button>
            </div>
          )}

          {/* Quick Actions */}
          <div className="border-t border-border pt-4 mt-4">
            <div className="text-sm font-medium text-text-secondary mb-3 px-1">
              Quick Actions
            </div>
            <div className="space-y-2">
              <Link
                to="/"
                className="flex items-center space-x-3 px-4 py-3 text-sm text-text-secondary hover:text-primary hover:bg-gray-50 rounded-lg transition-all duration-200"
                onClick={() => setIsHovered(false)}
              >
                <Icon name="Home" size={18} />
                <span>Dashboard</span>
              </Link>
              <Link
                to="/pricing"
                className="flex items-center space-x-3 px-4 py-3 text-sm text-text-secondary hover:text-primary hover:bg-gray-50 rounded-lg transition-all duration-200"
                onClick={() => setIsHovered(false)}
              >
                <Icon name="DollarSign" size={18} />
                <span>Pricing</span>
              </Link>
                                        <Link
                            to="/docs"
                            className="flex items-center space-x-3 px-4 py-3 text-sm text-text-secondary hover:text-primary hover:bg-gray-50 rounded-lg transition-all duration-200"
                            onClick={() => setIsHovered(false)}
                          >
                            <Icon name="Code" size={18} />
                            <span>API Docs</span>
                          </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar; 